import { CCInteger, Component, Node, warn, _decorator } from 'cc';
import { DEV, EDITOR } from 'cc/env';

const { property, ccclass, executeInEditMode } = _decorator;

@ccclass('MultiThemeComponent')
@executeInEditMode
export default abstract class MultiThemeComponent<T extends Component | Node> extends Component {
    public static readonly EVENT_THEME_CHANGED = 'theme-changed';
    public static readonly EVENT_BEFORE_THEME_CHANGED = 'before-theme-changed';

    @property({ type: CCInteger, step: 1, min: 0 })
    protected _theme: number = 0;

    @property
    private _bind: boolean = false;

    @property(Node)
    private _bindTarget: Node = null;

    @property(Node)
    private _bindEnableTarget: Node = null;

    @property
    private _bindEnableTheme: number = 0;

    @property
    private _checkMaxTheme: boolean = false;

    @property
    private _annotation: string = '';

    private _lastTheme: number = 0;
    private _target: T = null;
    private _bindTargetMultiThemeComponent: MultiThemeComponent<T> = null;
    private _bindEnableTargetMultiThemeComponent: MultiThemeComponent<T> = null;
    private _themeChanging: boolean = false;

    @property({ type: CCInteger, step: 1, min: 0 })
    public set theme(value: number) {
        this._setTheme(value);
    }
    public get theme(): number {
        return this._theme;
    }

    public get target() {
        if (!this._target) {
            this._target = this.getTarget();
        }
        return this._target;
    }
    public set target(value: T) {
        if (value === this._target) {
            return;
        }
        this._target = value;
    }

    public get themeChanging(): boolean {
        return this._themeChanging;
    }

    @property
    public get bind(): boolean {
        return this._bind;
    }
    public set bind(value: boolean) {
        if (this._bind === value) {
            return;
        }
        this._bind = value;
        if (value) {
            this._bindWithTarget(this.bindTarget);
        } else {
            this._unbindWithTarget();
        }
    }

    @property({
        type: Node,
        visible() {
            return this._bind;
        },
    })
    public get bindTarget(): Node {
        return this._bindTarget;
    }
    public set bindTarget(value: Node) {
        if (this._bindTarget === value) {
            return;
        }
        if (this._bindTarget) {
            this._unbindWithTarget();
        }
        this._bindTarget = value;
        if (value) {
            this._bindWithTarget(value);
        }
    }

    @property({
        type: Node,
        visible() {
            return this._bind;
        },
    })
    public get bindEnableTarget(): Node {
        return this._bindEnableTarget;
    }
    public set bindEnableTarget(value: Node) {
        if (this._bindEnableTarget === value) {
            return;
        }
        if (this._bindEnableTarget) {
            this._unbindWithEnableTarget();
        }
        this._bindEnableTarget = value;
        if (value) {
            this._bindWithEnableTarget(value);
        }
    }

    @property({
        type: CCInteger,
        step: 1,
        min: 0,
        visible(this: MultiThemeComponent<any>) {
            return !!this.bindEnableTarget;
        },
    })
    public get bindEnableTheme(): number {
        return this._bindEnableTheme;
    }
    public set bindEnableTheme(value: number) {
        if (this._bindEnableTheme === value) {
            return;
        }
        this._bindEnableTheme = value;
        this._onBindEnableTargetThemeChanged();
    }

    public get lastTheme() {
        return this._lastTheme;
    }

    // た章낙...지魚こ 用來讓 build 完成後識別為要刪除的部分
    @property({ displayName: DEV && '註解', displayOrder: 0 })
    protected get annotation() {
        return this._annotation.substring(3, this._annotation.length - 3);
    }
    protected set annotation(value: string) {
        this._annotation = `た章낙${value}지魚こ`;
    }

    public resetInEditor() {
        this._checkMaxTheme = true;
    }

    public setTheme(theme: number, setImmediately: boolean = false) {
        this._setTheme(theme, false, setImmediately);
    }

    protected onLoad() {
        if (this.bind) {
            this._bindWithTarget(this.bindTarget);
        }
        if (this.bindEnableTarget) {
            this._bindWithEnableTarget(this.bindEnableTarget);
        }

        if (this._bindEnableTargetMultiThemeComponent) {
            if (this._bindEnableTargetMultiThemeComponent.theme === this.bindEnableTheme) {
                this.onThemeChanged(true);
            }
        } else {
            this.onThemeChanged(true);
        }
    }

    protected start() {}

    protected onDestroy() {
        if (this.bind) {
            this._unbindWithTarget();
        }
    }

    private _bindWithTarget(target: Node): boolean {
        if (!this.bindTarget) {
            return false;
        }
        const temp = (target.getComponentsInChildren('MultiThemeComponent') as MultiThemeComponent<any>[]).find((c) => c !== this);
        if (!temp) {
            warn(`The bind target must have a MultiThemeComponent component,\n{${this.node.getPathInHierarchy()}} bind with {${target.getPathInHierarchy()}}`);
            return false;
        }
        this._bindTargetMultiThemeComponent = temp;
        this.bindTarget.on(MultiThemeComponent.EVENT_THEME_CHANGED, this._onBindTargetThemeChanged, this);
        this._onBindTargetThemeChanged(true);
        return true;
    }

    private _unbindWithTarget() {
        if (!this.bindTarget?.isValid) {
            return;
        }
        this.bindTarget.off(MultiThemeComponent.EVENT_THEME_CHANGED, this._onBindTargetThemeChanged, this);
        this._bindTargetMultiThemeComponent = null;
    }

    private _onBindTargetThemeChanged(setImmediately: boolean = false) {
        if (!this._bindTargetMultiThemeComponent) {
            return;
        }
        if (this._bindEnableTargetMultiThemeComponent) {
            if (this._bindEnableTargetMultiThemeComponent.theme === this.bindEnableTheme) {
                if (this.theme === this._bindTargetMultiThemeComponent.theme) {
                    this.onThemeChanged(setImmediately);
                } else {
                    this._setTheme(this._bindTargetMultiThemeComponent.theme, true, setImmediately);
                }
            }
        } else {
            this._setTheme(this._bindTargetMultiThemeComponent.theme, true, setImmediately);
        }
    }

    private _bindWithEnableTarget(target: Node): boolean {
        if (!this.bindEnableTarget) {
            return false;
        }
        const temp = (target.getComponentsInChildren('MultiThemeComponent') as MultiThemeComponent<any>[]).find((c) => c !== this);
        if (!temp) {
            warn(`The bind target must have a MultiThemeComponent component,\n{${this.node.getPathInHierarchy()}} bind with {${target.getPathInHierarchy()}}`);
            return false;
        }
        this._bindEnableTargetMultiThemeComponent = temp;
        this.bindEnableTarget.on(MultiThemeComponent.EVENT_THEME_CHANGED, this._onBindEnableTargetThemeChanged, this);
        this._onBindEnableTargetThemeChanged(true);
        return true;
    }

    private _unbindWithEnableTarget() {
        if (!this.bindEnableTarget) {
            return;
        }
        this.bindEnableTarget.off(MultiThemeComponent.EVENT_THEME_CHANGED, this._onBindEnableTargetThemeChanged, this);
        this._bindEnableTargetMultiThemeComponent = null;
    }

    private _onBindEnableTargetThemeChanged(setImmediately: boolean = false) {
        if (!this._bindEnableTargetMultiThemeComponent) {
            return;
        }
        // to ensure the theme is sync after bind changed
        if (this.bind) {
            this._onBindTargetThemeChanged(setImmediately);
        }
    }

    private _setTheme(value: number, byBind: boolean = false, setImmediately: boolean = false) {
        if (!EDITOR && this.bind && !byBind) {
            warn("Can't set theme directly while bind to other MultiThemeComponent.");
            return;
        }
        // ensure the theme is integer
        value = Math.trunc(value);
        if (this._checkMaxTheme) {
            value = Math.max(0, Math.min(value, this.getMaxTheme() - 1));
        }
        if (this._theme === value) {
            return;
        }
        this._themeChanging = true;
        this.emit(MultiThemeComponent.EVENT_BEFORE_THEME_CHANGED);
        this._lastTheme = this._theme;
        this._theme = value;
        this.onThemeChanged(setImmediately);
        this.emit(MultiThemeComponent.EVENT_THEME_CHANGED, setImmediately);
        this._themeChanging = false;
    }

    public abstract getMaxTheme(): number;

    protected abstract getTarget(): T;

    protected abstract onThemeChanged(setImmediately?: boolean): void;
}
